import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  BulkImportResult,
  BulkImportRow,
  BulkMovePrepConfigItemsPayload,
  CreatePrepConfigItemPayload,
  CreatePrepConfigSectionPayload,
  ManagerPrepType,
  PrepConfigResponse,
  PrepConfigSection,
  UnmappedIngredient,
  PrepConfigIngredientHealth,
  PrepConfigSourceIssue,
  SeedReconciliationLog,
  UpdatePrepConfigItemPayload,
  UpdatePrepConfigSectionPayload
} from '@/src/types/apps/prepConfig'

type HttpClient = {
  get: Function
  post: Function
  put: Function
  patch: Function
  delete: Function
}

function unwrap<T>(data: unknown): T {
  let current: unknown = data
  const outer = current as { data?: unknown }
  if (outer && typeof outer === 'object' && 'data' in outer) {
    current = outer.data
  }

  // AuditableResponse from Nest controllers: { data: T, _metadata } — already
  // wrapped once more by ResponseTransformInterceptor.
  const auditable = current as { data?: T; _metadata?: unknown }
  if (auditable && typeof auditable === 'object' && 'data' in auditable && '_metadata' in auditable) {
    current = auditable.data
  }

  return current as T
}

function sectionsKey(
  prepType: ManagerPrepType
): keyof Pick<PrepConfigResponse, 'cookPrep' | 'linePrep' | 'onHandPrep'> {
  if (prepType === 'COOK') return 'cookPrep'
  if (prepType === 'LINE') return 'linePrep'

  return 'onHandPrep'
}

function sortSections(sections: PrepConfigSection[]) {
  sections.sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id)
}

function sortItems(section: PrepConfigSection) {
  section.items.sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id)
}

interface PrepConfigState {
  config: PrepConfigResponse | null
  unmappedIngredients: UnmappedIngredient[]
  sourceIssues: PrepConfigSourceIssue[]
  catalogueSyncedAt: string | null
  unmappedLoading: boolean
  reconciliationLog: SeedReconciliationLog | null
  reconciliationLoading: boolean
  loading: boolean
  saving: boolean
  error: string | null
  success: string | null
}

const initialState: PrepConfigState = {
  config: null,
  unmappedIngredients: [],
  sourceIssues: [],
  catalogueSyncedAt: null,
  unmappedLoading: false,
  reconciliationLog: null,
  reconciliationLoading: false,
  loading: false,
  saving: false,
  error: null,
  success: null
}

export const fetchPrepConfig = createAsyncThunk<
  PrepConfigResponse,
  void,
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/fetchPrepConfig', async (_, { rejectWithValue, extra }) => {
  try {
    const { data } = await extra.httpClient.get('/preplists/config')

    return unwrap<PrepConfigResponse>(data)
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to load prep config')
  }
})

export const fetchUnmappedIngredients = createAsyncThunk<
  PrepConfigIngredientHealth,
  void,
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/fetchUnmappedIngredients', async (_, { rejectWithValue, extra }) => {
  try {
    const { data } = await extra.httpClient.get('/preplists/config/unmapped-ingredients')
    const health = unwrap<PrepConfigIngredientHealth | UnmappedIngredient[]>(data)

    // Back-compat if an older BE still returns a bare array.
    if (Array.isArray(health)) {
      return { catalogueSyncedAt: null, unmapped: health, sourceIssues: [] }
    }

    return {
      catalogueSyncedAt: health.catalogueSyncedAt ?? null,
      unmapped: Array.isArray(health.unmapped) ? health.unmapped : [],
      sourceIssues: Array.isArray(health.sourceIssues) ? health.sourceIssues : []
    }
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to load unmapped ingredients')
  }
})

export const fetchSeedReconciliationLog = createAsyncThunk<
  SeedReconciliationLog,
  void,
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/fetchSeedReconciliationLog', async (_, { rejectWithValue, extra }) => {
  try {
    const { data } = await extra.httpClient.get('/preplists/config/seed-reconciliation-log')

    return unwrap<SeedReconciliationLog>(data)
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to load reconciliation log')
  }
})

export const createPrepConfigSection = createAsyncThunk<
  PrepConfigSection,
  CreatePrepConfigSectionPayload,
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/createPrepConfigSection', async (payload, { rejectWithValue, extra }) => {
  try {
    const { data } = await extra.httpClient.post('/preplists/config/sections', payload)

    return unwrap<PrepConfigSection>(data)
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to create section')
  }
})

export const updatePrepConfigSection = createAsyncThunk<
  PrepConfigSection,
  { sectionId: number; payload: UpdatePrepConfigSectionPayload },
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/updatePrepConfigSection', async ({ sectionId, payload }, { rejectWithValue, extra }) => {
  try {
    const { data } = await extra.httpClient.put(`/preplists/config/sections/${sectionId}`, payload)

    return unwrap<PrepConfigSection>(data)
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to update section')
  }
})

export const deletePrepConfigSection = createAsyncThunk<
  number,
  number,
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/deletePrepConfigSection', async (sectionId, { rejectWithValue, extra }) => {
  try {
    await extra.httpClient.delete(`/preplists/config/sections/${sectionId}`)

    return sectionId
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to delete section')
  }
})

export const createPrepConfigItem = createAsyncThunk<
  void,
  { sectionId: number; payload: CreatePrepConfigItemPayload },
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/createPrepConfigItem', async ({ sectionId, payload }, { rejectWithValue, extra }) => {
  try {
    await extra.httpClient.post(`/preplists/config/sections/${sectionId}/items`, payload)
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to create item')
  }
})

export const updatePrepConfigItem = createAsyncThunk<
  void,
  { itemId: number; payload: UpdatePrepConfigItemPayload },
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/updatePrepConfigItem', async ({ itemId, payload }, { rejectWithValue, extra }) => {
  try {
    await extra.httpClient.put(`/preplists/config/items/${itemId}`, payload)
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to update item')
  }
})

export const deletePrepConfigItem = createAsyncThunk<
  number,
  number,
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/deletePrepConfigItem', async (itemId, { rejectWithValue, extra }) => {
  try {
    await extra.httpClient.delete(`/preplists/config/items/${itemId}`)

    return itemId
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to delete item')
  }
})

export const bulkImportPrepConfig = createAsyncThunk<
  BulkImportResult,
  BulkImportRow[],
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/bulkImportPrepConfig', async (rows, { rejectWithValue, extra }) => {
  try {
    const { data } = await extra.httpClient.post('/preplists/config/bulk-import', { rows })

    return unwrap<BulkImportResult>(data)
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to bulk import')
  }
})

export const bulkMovePrepConfigItems = createAsyncThunk<
  { moved: number },
  BulkMovePrepConfigItemsPayload,
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/bulkMovePrepConfigItems', async (payload, { rejectWithValue, extra }) => {
  try {
    const { data } = await extra.httpClient.patch('/preplists/config/items/bulk-move', payload)

    return unwrap<{ moved: number }>(data)
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to move items')
  }
})

export const reorderPrepConfigSections = createAsyncThunk<
  void,
  { prepType: ManagerPrepType; sectionAId: number; sectionBId: number; sortOrderA: number; sortOrderB: number },
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/reorderPrepConfigSections', async (payload, { rejectWithValue, extra }) => {
  try {
    await Promise.all([
      extra.httpClient.put(`/preplists/config/sections/${payload.sectionAId}`, {
        sortOrder: payload.sortOrderB
      }),
      extra.httpClient.put(`/preplists/config/sections/${payload.sectionBId}`, {
        sortOrder: payload.sortOrderA
      })
    ])
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to reorder sections')
  }
})

export const reorderPrepConfigItems = createAsyncThunk<
  void,
  { sectionId: number; itemAId: number; itemBId: number; sortOrderA: number; sortOrderB: number },
  { rejectValue: string; extra: { httpClient: HttpClient } }
>('prepConfig/reorderPrepConfigItems', async (payload, { rejectWithValue, extra }) => {
  try {
    await Promise.all([
      extra.httpClient.put(`/preplists/config/items/${payload.itemAId}`, {
        sortOrder: payload.sortOrderB
      }),
      extra.httpClient.put(`/preplists/config/items/${payload.itemBId}`, {
        sortOrder: payload.sortOrderA
      })
    ])
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to reorder items')
  }
})

const prepConfigSlice = createSlice({
  name: 'prepConfig',
  initialState,
  reducers: {
    resetPrepConfigMessages(state) {
      state.error = null
      state.success = null
    },
    swapSectionSortOrders(state, action: PayloadAction<{ prepType: ManagerPrepType; indexA: number; indexB: number }>) {
      if (!state.config) return
      const key = sectionsKey(action.payload.prepType)
      const sections = state.config[key]
      const { indexA, indexB } = action.payload
      const a = sections[indexA]
      const b = sections[indexB]
      if (!a || !b) return
      const aOrder = a.sortOrder
      a.sortOrder = b.sortOrder
      b.sortOrder = aOrder
      sections[indexA] = b
      sections[indexB] = a
      sortSections(sections)
    },
    swapItemSortOrders(state, action: PayloadAction<{ sectionId: number; indexA: number; indexB: number }>) {
      if (!state.config) return
      const all = [...state.config.cookPrep, ...state.config.linePrep, ...state.config.onHandPrep]
      const section = all.find(s => s.id === action.payload.sectionId)
      if (!section) return
      const { indexA, indexB } = action.payload
      const a = section.items[indexA]
      const b = section.items[indexB]
      if (!a || !b) return
      const aOrder = a.sortOrder
      a.sortOrder = b.sortOrder
      b.sortOrder = aOrder
      section.items[indexA] = b
      section.items[indexB] = a
      sortItems(section)
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchPrepConfig.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPrepConfig.fulfilled, (state, action) => {
        state.loading = false
        state.config = action.payload
      })
      .addCase(fetchPrepConfig.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? 'Failed to load prep config'
      })

    builder
      .addCase(fetchUnmappedIngredients.pending, state => {
        state.unmappedLoading = true
      })
      .addCase(fetchUnmappedIngredients.fulfilled, (state, action) => {
        state.unmappedLoading = false
        state.unmappedIngredients = action.payload.unmapped
        state.sourceIssues = action.payload.sourceIssues
        state.catalogueSyncedAt = action.payload.catalogueSyncedAt
      })
      .addCase(fetchUnmappedIngredients.rejected, (state, action) => {
        state.unmappedLoading = false
        state.error = action.payload ?? 'Failed to load unmapped ingredients'
      })

    builder
      .addCase(fetchSeedReconciliationLog.pending, state => {
        state.reconciliationLoading = true
      })
      .addCase(fetchSeedReconciliationLog.fulfilled, (state, action) => {
        state.reconciliationLoading = false
        state.reconciliationLog = action.payload
      })
      .addCase(fetchSeedReconciliationLog.rejected, (state, action) => {
        state.reconciliationLoading = false
        state.error = action.payload ?? 'Failed to load reconciliation log'
      })

    const saving = (state: PrepConfigState) => {
      state.saving = true
      state.error = null
    }
    const saved = (state: PrepConfigState, message: string) => {
      state.saving = false
      state.success = message
    }
    const saveFailed = (state: PrepConfigState, action: { payload?: string }) => {
      state.saving = false
      state.error = action.payload ?? 'Save failed'
    }
    const silentDone = (state: PrepConfigState) => {
      state.saving = false
    }

    builder
      .addCase(createPrepConfigSection.pending, saving)
      .addCase(createPrepConfigSection.fulfilled, (state, action) => {
        saved(state, `Section "${action.payload.name}" created`)
      })
      .addCase(createPrepConfigSection.rejected, saveFailed)

    builder
      .addCase(updatePrepConfigSection.pending, saving)
      .addCase(updatePrepConfigSection.fulfilled, (state, action) => {
        saved(state, `Section "${action.payload.name}" updated`)
      })
      .addCase(updatePrepConfigSection.rejected, saveFailed)

    builder
      .addCase(deletePrepConfigSection.pending, saving)
      .addCase(deletePrepConfigSection.fulfilled, state => {
        saved(state, 'Section deleted')
      })
      .addCase(deletePrepConfigSection.rejected, saveFailed)

    builder
      .addCase(createPrepConfigItem.pending, saving)
      .addCase(createPrepConfigItem.fulfilled, state => {
        saved(state, 'Item mapped')
      })
      .addCase(createPrepConfigItem.rejected, saveFailed)

    builder
      .addCase(updatePrepConfigItem.pending, saving)
      .addCase(updatePrepConfigItem.fulfilled, state => {
        saved(state, 'Item updated')
      })
      .addCase(updatePrepConfigItem.rejected, saveFailed)

    builder
      .addCase(deletePrepConfigItem.pending, saving)
      .addCase(deletePrepConfigItem.fulfilled, state => {
        saved(state, 'Item deleted')
      })
      .addCase(deletePrepConfigItem.rejected, saveFailed)

    builder
      .addCase(bulkImportPrepConfig.pending, saving)
      .addCase(bulkImportPrepConfig.fulfilled, (state, action) => {
        const { sectionsCreated, itemsCreated, itemsUpdated } = action.payload
        saved(
          state,
          `Bulk import complete: ${sectionsCreated} section(s) created, ${itemsCreated} item(s) created, ${itemsUpdated} item(s) updated`
        )
      })
      .addCase(bulkImportPrepConfig.rejected, saveFailed)

    builder
      .addCase(bulkMovePrepConfigItems.pending, saving)
      .addCase(bulkMovePrepConfigItems.fulfilled, (state, action) => {
        saved(state, `Moved ${action.payload.moved} item(s)`)
      })
      .addCase(bulkMovePrepConfigItems.rejected, saveFailed)

    builder
      .addCase(reorderPrepConfigSections.pending, saving)
      .addCase(reorderPrepConfigSections.fulfilled, silentDone)
      .addCase(reorderPrepConfigSections.rejected, saveFailed)

    builder
      .addCase(reorderPrepConfigItems.pending, saving)
      .addCase(reorderPrepConfigItems.fulfilled, silentDone)
      .addCase(reorderPrepConfigItems.rejected, saveFailed)
  }
})

export const { resetPrepConfigMessages, swapSectionSortOrders, swapItemSortOrders } = prepConfigSlice.actions
export default prepConfigSlice.reducer
